/**
 * Auth Step Tracking & Error Source Classification
 *
 * Provides types and pure functions for tracking authentication progress
 * and accurately attributing errors to the correct service (monday.com,
 * backend/Supabase, or network).
 */

// ============================================================================
// Types
// ============================================================================

export type AuthErrorSource = 'monday' | 'backend' | 'network' | 'not_installed' | 'unknown';

export type AuthStepId =
  | 'authenticating'
  | 'loading_workspace'
  | 'verifying_subscription'
  | 'preparing_dashboard';

export type AuthStepStatus = 'pending' | 'active' | 'done' | 'failed';

export interface AuthStep {
  id: AuthStepId;
  label: string;
  status: AuthStepStatus;
}

// ============================================================================
// Step Helpers
// ============================================================================

const STEP_DEFINITIONS: { id: AuthStepId; label: string }[] = [
  { id: 'authenticating', label: 'Authenticating with monday.com' },
  { id: 'loading_workspace', label: 'Loading workspace' },
  { id: 'verifying_subscription', label: 'Verifying subscription' },
  { id: 'preparing_dashboard', label: 'Preparing dashboard' },
];

export function createInitialSteps(): AuthStep[] {
  return STEP_DEFINITIONS.map(def => ({
    ...def,
    status: 'pending' as AuthStepStatus,
  }));
}

export function advanceToStep(steps: AuthStep[], stepId: AuthStepId): AuthStep[] {
  const targetIndex = STEP_DEFINITIONS.findIndex(d => d.id === stepId);
  return steps.map((step, i) => ({
    ...step,
    status: i < targetIndex ? 'done' : i === targetIndex ? 'active' : step.status,
  }));
}

export function markStepFailed(steps: AuthStep[], stepId: AuthStepId): AuthStep[] {
  return steps.map(step => ({
    ...step,
    status: step.id === stepId ? 'failed' : step.status,
  }));
}

export function markAllDone(steps: AuthStep[]): AuthStep[] {
  return steps.map(step => ({ ...step, status: 'done' as AuthStepStatus }));
}

// ============================================================================
// Error Source Classification
// ============================================================================

const NETWORK_ERROR_PATTERNS = [
  'failed to fetch',
  'networkerror',
  'network error',
  'net::err_',
  'econnrefused',
  'econnreset',
  'enotfound',
  'load failed',
  'typeerror: fetch',
  'typeerror: network',
  'the internet connection appears to be offline',
  'a]network connection was lost',
];

/**
 * Classify the error source based on the error message and the step that was
 * active when the error occurred.
 *
 * Network errors are detected by message content first (takes precedence).
 * Otherwise, the active step determines attribution:
 *   - 'authenticating' → monday.com (SDK/API calls)
 *   - 'loading_workspace' / 'verifying_subscription' / 'preparing_dashboard' → backend
 */
export function classifyErrorSource(
  error: unknown,
  currentStep: AuthStepId
): AuthErrorSource {
  const message =
    error instanceof Error ? error.message : String(error ?? '');
  const lower = message.toLowerCase();

  // App not installed — non-retryable, distinct from other errors
  if (lower.includes('app_not_installed') || lower.includes('app is not installed')) {
    return 'not_installed';
  }

  // Network errors take precedence
  if (NETWORK_ERROR_PATTERNS.some(pattern => lower.includes(pattern))) {
    return 'network';
  }

  // Step-based attribution
  switch (currentStep) {
    case 'authenticating':
      return 'monday';
    case 'loading_workspace':
    case 'verifying_subscription':
    case 'preparing_dashboard':
      return 'backend';
    default:
      return 'unknown';
  }
}

/**
 * Most error sources are retryable — except permanent states like not_installed.
 */
export function isSourceRetryable(source: AuthErrorSource): boolean {
  return source !== 'not_installed';
}

// ============================================================================
// Error Content (displayed in AuthLoadingScreen)
// ============================================================================

export interface ErrorContent {
  title: string;
  description: string;
  reassurance: string;
  footerText: string;
}

export const ERROR_CONTENT: Record<AuthErrorSource, ErrorContent> = {
  backend: {
    title: "We're experiencing a temporary disruption",
    description:
      'Our servers are taking a moment to respond. This is usually resolved quickly.',
    reassurance:
      'Your data is safe and all scheduled tasks will continue to run as expected.',
    footerText: 'If this persists, check status.taskloops.com for updates.',
  },
  monday: {
    title: "monday.com's API is temporarily unavailable",
    description:
      "We're unable to reach monday.com right now. This is usually a brief interruption on their end.",
    reassurance:
      'Scheduled tasks will automatically retry, and no data has been lost.',
    footerText: 'You can also check status.monday.com for updates.',
  },
  not_installed: {
    title: 'TaskLoops is not installed',
    description:
      'This app is no longer installed on your monday.com account.',
    reassurance:
      'To continue using TaskLoops, please reinstall it from the monday.com marketplace.',
    footerText: 'If you believe this is an error, try refreshing the page.',
  },
  network: {
    title: 'Connection interrupted',
    description:
      "We can't reach the server. Try checking your internet connection.",
    reassurance:
      'Your data is safe. Everything will pick up right where it left off once reconnected.',
    footerText: 'Try refreshing the page after checking your connection.',
  },
  unknown: {
    title: 'Something went wrong',
    description: 'An unexpected error occurred while loading TaskLoops.',
    reassurance:
      'Your data is safe and all scheduled tasks will continue to run as expected.',
    footerText: 'If this problem persists, please contact support.',
  },
};
